import { FileItem } from 'ng2-file-upload';
import { max, sumBy } from 'lodash';
import { IrisInfrastructureService } from '@iris/common/services/infrastructure.service';
import { AuthFileUploader, AuthFileUploaderOptions } from '@iris/common/modules/file-uploader/auth-file-uploader';
import { AuthFacade } from '@iris/modules/auth/utils/auth.facade';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class BulkFileUploaderFactory {
  private readonly authFacade = inject(AuthFacade);
  private readonly infrastructureService = inject(IrisInfrastructureService);

  instance(options: AuthFileUploaderOptions): BulkFileUploader {
    return new BulkFileUploader(this.authFacade, this.infrastructureService, options);
  }
}

export class BulkFileUploader extends AuthFileUploader {
  private readonly UPLOADING_AMOUNT_PER_SECOND = 3 * 1024 * 1024;
  private readonly PROGRESS_INTERVAL = 500;

  private readonly MILLISECONDS_IN_SECOND = 1000;
  private readonly MINIMUM_UPLOADING_TIME_IN_SECONDS = 10;
  private readonly DEFAULT_PROGRESS_INCREMENT = 0.5;
  private progressIncrement = 0.5;

  private bulkUploadSubscription: Subscription;

  constructor(
    authFacade: AuthFacade,
    infrastructureService: IrisInfrastructureService,
    options: AuthFileUploaderOptions,
  ) {
    super(authFacade, infrastructureService, options);
  }

  uploadAllBulk(): void {
    const xhr = new XMLHttpRequest();
    const sendable = new FormData();
    const fakeItem: FileItem = null;
    this.onBuildItemForm(fakeItem, sendable);

    const cancelAll = this.cancelAll;
    const cancelItem = this.cancelItem;

    this._setProgressIncrement();
    this._setProgress(0);

    const itemsToUpload = this.queue.filter(item => !item.isReady && !item.isUploading);

    itemsToUpload.forEach(item => {
      item.isReady = true;
      item.isUploading = true;
      item.isUploaded = false;
      item.isSuccess = false;
      item.isCancel = false;
      item.isError = false;
      item.progress = 0;
      item._xhr = xhr;

      if (typeof item._file.size !== 'number') {
        throw new TypeError('The file specified is no longer valid');
      }
      sendable.append(this.options.itemAlias ?? 'file', item._file, item.file.name);
    });

    if (this.options.additionalParameter !== undefined) {
      Object.keys(this.options.additionalParameter).forEach((key) => {
        sendable.append(key, this.options.additionalParameter[key]);
      });
    }

    xhr.onerror = () => {
      this.queue.forEach(item => {
        this._onErrorItem(item, null, xhr.status, null);
      });
      this.cancelAll = cancelAll;
      this.cancelItem = cancelItem;
      this._setProgress(0);
    };

    xhr.onabort = () => {
      this.queue.forEach(item => {
        this._onCancelItem(item, null, xhr.status, null);
      });
      this.cancelAll = cancelAll;
      this.cancelItem = cancelItem;
      this.cancelAll();
      this._setProgress(0);
    };

    xhr.onload = () => {
      const headers = this._parseHeaders(xhr.getAllResponseHeaders());
      const response = this._transformResponse(xhr.response);
      const status = this._isSuccessCode(xhr.status) ? 'Success' : 'Error';
      const method = `_on${status}Item`;
      itemsToUpload.forEach(item => {
        this[method](item, response, xhr.status, headers);
        this._onCompleteItem(item, response, xhr.status, headers);
      });

      this._setProgress(100);
      this._render();
      this.cancelAll = cancelAll;
      this.cancelItem = cancelItem;
    };

    this.cancelAll = () => {
      xhr.abort();
      this.bulkUploadSubscription?.unsubscribe();
      this.bulkUploadSubscription = null;
    };

    this.cancelItem = (file: FileItem) => {
      if (file._xhr === xhr) {
        this.cancelItemBulk(file);
      } else {
        cancelItem(file);
      }
    };

    xhr.open('POST', this.options.url, true);
    xhr.withCredentials = true;

    if (this.options.headers) {
      this.options.headers?.forEach(header => {
        xhr.setRequestHeader(header.name, header.value);
      });
    }

    this.bulkUploadSubscription?.unsubscribe();
    this.bulkUploadSubscription = this.authFacade.actualToken(true).pipe(
      tap((authToken) => {
        if (authToken) {
          this.authToken = `Bearer ${authToken}`;
          xhr.setRequestHeader(this.authTokenHeader, `Bearer ${authToken}`);
        }

        xhr.send(sendable);

        this._increaseProgress();
      }),
    ).subscribe();
  }

  markFileCanceled(item: FileItem): void {
    this._onCancelItem(item, null, null, null);
  }

  private cancelItemBulk(file: FileItem): void {
    const index = this.getIndexOfItem(file);
    const item = this.queue[index];
    if (!!item && item.isUploading) {
      item._xhr.abort();
      this.bulkUploadSubscription?.unsubscribe();
      this.bulkUploadSubscription = null;
    }
  }

  private _increaseProgress(): void {
    if (this.progress >= 95) { return; }
    this._setProgress(this.progress + this.progressIncrement);
    setTimeout(() => this._increaseProgress(), this.PROGRESS_INTERVAL);
  }

  private _setProgress(progress: number): void {
    this.progress = progress;
    for (const item of this.queue) {
      this.onProgressItem(item, this.progress);
    }
  }

  private _setProgressIncrement(reset = false): void {
    if (reset) {
      this.progressIncrement = this.DEFAULT_PROGRESS_INCREMENT;
    }
    const totalSize = sumBy(this.queue, item => item.file.size);
    const secondsToUpload = max([totalSize / this.UPLOADING_AMOUNT_PER_SECOND, this.MINIMUM_UPLOADING_TIME_IN_SECONDS]);
    const totalSteps = secondsToUpload / (this.PROGRESS_INTERVAL / this.MILLISECONDS_IN_SECOND);
    this.progressIncrement = 100 / totalSteps;
  }
}
